import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useIsMobile from "../hooks/useIsMobile";
import Reveal from "../components/common/Reveal";
import SectionTag from "../components/common/SectionTag";
import Icon from "../components/common/Icon";
import SocialIcon from "../components/common/SocialIcon";
import Seo from "../components/common/Seo";
import { BRAND, SCRIPT_URL, SOCIAL } from "../data/constants";
import { colors, fonts, emphasis, sectionPad, maxW } from "../styles/theme";

const inputStyle = {
  width: "100%", background: colors.paper, border: `1px solid ${colors.borderStrong}`, borderRadius: 3,
  padding: "12px 15px", color: colors.ink, fontFamily: fonts.body, fontSize: 14, outline: "none",
};
const labelStyle = { display: "block", fontFamily: fonts.body, fontSize: 12.5, fontWeight: 600, color: colors.inkDim, marginBottom: 7 };

export default function ContactPage() {
  const mob = useIsMobile();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", msg: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.name || !form.email || !form.msg) {
      setError("Please fill in Name, Email and Message.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await fetch(SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, type: "Contact" }),
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: colors.paper, minHeight: "100vh", paddingTop: 74 }}>
      <Seo
        title="Contact | Charith"
        description="Get in touch with Charith to start a project or ask a question — we typically reply within 24 hours."
        path="/contact"
      />
      <section style={{ background: colors.paper, ...sectionPad(mob) }}>
        <Reveal style={{ ...maxW, maxWidth: 720 }}>
          <SectionTag>Get In Touch</SectionTag>
          <h1 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 32 : "clamp(30px,4.2vw,54px)", lineHeight: 1.12, color: colors.ink, letterSpacing: "-.01em" }}>Let's <span style={emphasis}>talk</span></h1>
        </Reveal>
      </section>

      <section style={{ background: colors.paper, padding: mob ? "0 6% 56px" : "0 6% 96px" }}>
        <div style={{ ...maxW, display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1.4fr", gap: mob ? 40 : 60, alignItems: "start" }}>

          <Reveal>
            <h2 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: mob ? 20 : 24, color: colors.ink, marginBottom: 28 }}>Contact Information</h2>
            <div style={{ display: "flex", gap: 14, marginBottom: 24, alignItems: "flex-start" }}>
              <div style={{ width: 42, height: 42, borderRadius: 3, border: `1px solid ${colors.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="Mail" size={17} style={{ color: colors.rust }} />
              </div>
              <div>
                <p style={{ color: colors.inkFainter, fontFamily: fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: ".12em", marginBottom: 4 }}>EMAIL</p>
                <button onClick={() => window.open(`mailto:${BRAND.email}`, "_self")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: colors.ink, fontFamily: fonts.body, fontSize: mob ? 13.5 : 14.5, fontWeight: 500, wordBreak: "break-all", textAlign: "left" }}>{BRAND.email}</button>
              </div>
            </div>
            <div style={{ marginTop: 30 }}>
              <p style={{ color: colors.inkFainter, fontFamily: fonts.body, fontSize: 11, fontWeight: 700, letterSpacing: ".14em", marginBottom: 14 }}>FOLLOW US</p>
              <div style={{ display: "flex", gap: 10 }}>
                {SOCIAL.map((s) => (
                  <motion.button
                    key={s.label}
                    whileHover={{ backgroundColor: colors.ink, color: colors.cream, borderColor: colors.ink }}
                    onClick={() => window.open(s.href, "_blank")}
                    style={{ width: 40, height: 40, borderRadius: 3, background: "transparent", border: `1px solid ${colors.borderStrong}`, display: "flex", alignItems: "center", justifyContent: "center", color: colors.inkDim, cursor: "pointer" }}
                  >
                    <SocialIcon path={s.path} size={16} />
                  </motion.button>
                ))}
              </div>
            </div>
          </Reveal>

          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div key="sent" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center", background: colors.paperAlt, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, padding: mob ? 32 : 52 }}>
                <Icon name="CheckCircle2" size={44} style={{ color: colors.olive, marginBottom: 18 }} />
                <h3 style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 21, color: colors.ink, marginBottom: 10 }}>Message sent</h3>
                <p style={{ color: colors.inkDim, fontFamily: fonts.body, fontSize: 14 }}>We'll get back to you within 24 hours.</p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ background: colors.paperAlt, border: `1px solid ${colors.borderStrong}`, borderRadius: 6, padding: mob ? 22 : 38 }}>
                {[
                  { label: "Your Name *", key: "name", type: "text", ph: "Full name" },
                  { label: "Email Address *", key: "email", type: "email", ph: "your@email.com" },
                  { label: "Phone Number", key: "phone", type: "tel", ph: "+91 98765 43210" },
                  { label: "Subject", key: "subject", type: "text", ph: "How can we help?" },
                ].map((f) => (
                  <div key={f.key} style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>{f.label}</label>
                    <input type={f.type} placeholder={f.ph} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} style={inputStyle} />
                  </div>
                ))}
                <div style={{ marginBottom: 26 }}>
                  <label style={labelStyle}>Message *</label>
                  <textarea placeholder="Tell us about your project…" value={form.msg} onChange={(e) => setForm({ ...form, msg: e.target.value })}
                    rows={5} style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                {error && <p style={{ color: colors.rust, fontFamily: fonts.body, fontSize: 13, marginBottom: 16 }}>{error}</p>}
                <motion.button
                  whileHover={{ backgroundColor: loading ? colors.ink : colors.rust }}
                  onClick={submit}
                  disabled={loading}
                  style={{ width: "100%", background: colors.ink, border: "none", color: colors.cream, fontFamily: fonts.body, fontWeight: 700, fontSize: 15, padding: 16, borderRadius: 3, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Sending…" : "Send Message"}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}
